import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  interest: "",
  message: "",
};

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const INTERESTS = [
    t("servicesData.goToMarket.title"),
    t("servicesData.insights.title"),
    t("servicesData.abm.title"),
    t("servicesData.media.title"),
    t("servicesData.creative.title"),
    t("contact.generalEnquiry"),
  ];

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
      };
      const res = await axios.post(`${API}/contact`, payload);
      if (res.data && res.data.status === "received") {
        setSubmitted(true);
        setForm(initialForm);
        toast.success(t("contact.successToast"));
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("contact.errorGeneric", { email: "sales@revgrow.co.uk" });
      setError(typeof msg === "string" ? msg : t("contact.errorGeneric", { email: "sales@revgrow.co.uk" }));
      toast.error(t("contact.errorToast"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <section className="relative pt-24 md:pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_50%_at_80%_10%,rgba(192,94,76,0.14),transparent_70%)]" />
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("contact.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("contact.heroTitle1")}<br />
              {t("contact.heroTitle2")} <span className="italic text-clay-400">{t("contact.heroTitleItalic")}</span> {t("contact.heroTitle3")}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          {/* Left: contact info */}
          <div className="lg:col-span-5 space-y-12">
            <Reveal>
              <p className="body-lg max-w-md">{t("contact.description")}</p>
            </Reveal>

            <Reveal delay={80}>
              <div className="space-y-8">
                <InfoLine icon={<Mail size={18} className="text-clay-400" />} label={t("contact.email")}>
                  <a
                    href="mailto:sales@revgrow.co.uk"
                    className="font-display text-2xl text-bone-100 hover:text-clay-400 transition-colors"
                    data-testid={CONTACT.mailto}
                  >
                    sales@revgrow.co.uk
                  </a>
                </InfoLine>

                <InfoLine icon={<Phone size={18} className="text-clay-400" />} label={t("contact.phone")}>
                  <a href="tel:+493055235449" className="font-display text-2xl text-bone-100 hover:text-clay-400 transition-colors" data-testid="contact-phone">
                    +49 30 55235449
                  </a>
                </InfoLine>

                <InfoLine icon={<MapPin size={18} className="text-clay-400" />} label={t("contact.studio")}>
                  <p className="text-bone-100/90 leading-relaxed" data-testid="contact-address">
                    RevGrow UG (haftungsbeschränkt)<br />
                    Akazienstraße 3A<br />
                    10823 Berlin, Germany
                  </p>
                </InfoLine>
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="border-t border-white/10 pt-8 text-sm text-bone-200/60 leading-relaxed">
                {t("contact.routingNote", { email: "sales@revgrow.co.uk" })}
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <div className="card-surface p-8 md:p-12">
                {submitted ? (
                  <div data-testid={CONTACT.success} className="py-12 text-center">
                    <div className="w-14 h-14 rounded-full bg-clay-500/15 border border-clay-400/40 flex items-center justify-center mx-auto mb-8">
                      <Check size={24} className="text-clay-400" />
                    </div>
                    <h2 className="display-h3 mb-4">{t("contact.successTitle")}</h2>
                    <p className="text-bone-200/75 max-w-md mx-auto">
                      {t("contact.successBody", { email: "sales@revgrow.co.uk" })}
                    </p>
                    <button
                      className="btn-ghost mt-10"
                      onClick={() => setSubmitted(false)}
                      data-testid="contact-send-another"
                    >
                      {t("contact.sendAnother")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} data-testid={CONTACT.form} className="space-y-6" noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field label={t("contact.labelName")} name="name" value={form.name} onChange={onChange} required testid={CONTACT.inputName} />
                      <Field label={t("contact.labelEmail")} name="email" type="email" value={form.email} onChange={onChange} required testid={CONTACT.inputEmail} />
                      <Field label={t("contact.labelCompany")} name="company" value={form.company} onChange={onChange} testid={CONTACT.inputCompany} />
                      <Field label={t("contact.labelPhone")} name="phone" value={form.phone} onChange={onChange} testid={CONTACT.inputPhone} />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest2 text-bone-200/60 mb-3">{t("contact.labelInterest")}</label>
                      <select
                        name="interest"
                        value={form.interest}
                        onChange={onChange}
                        data-testid={CONTACT.inputInterest}
                        className="w-full bg-transparent border border-white/15 rounded-md px-4 py-3.5 text-bone-100 focus:outline-none focus:border-clay-400 transition-colors"
                      >
                        <option value="" className="bg-ink-800">{t("contact.selectService")}</option>
                        {INTERESTS.map((i) => (
                          <option key={i} value={i} className="bg-ink-800">{i}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest2 text-bone-200/60 mb-3">{t("contact.labelMessage")}</label>
                      <textarea
                        name="message"
                        rows={5}
                        value={form.message}
                        onChange={onChange}
                        required
                        data-testid={CONTACT.inputMessage}
                        className="w-full bg-transparent border border-white/15 rounded-md px-4 py-3.5 text-bone-100 placeholder:text-bone-200/30 focus:outline-none focus:border-clay-400 transition-colors resize-none"
                        placeholder={t("contact.messagePlaceholder")}
                      />
                    </div>

                    {error && (
                      <div data-testid={CONTACT.error} className="text-sm text-clay-400 border border-clay-400/30 bg-clay-500/5 rounded-md px-4 py-3">
                        {error}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid={CONTACT.submit}
                        className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? t("contact.sending") : t("contact.submit")}
                        <ArrowUpRight size={16} />
                      </button>
                      <p className="text-xs text-bone-200/50">
                        {t("contact.preferEmail")}{" "}
                        <a href="mailto:sales@revgrow.co.uk" className="text-clay-400 hover:underline">
                          sales@revgrow.co.uk
                        </a>
                        .
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoLine({ icon, label, children }) {
  return (
    <div className="flex items-start gap-5">
      <div className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest2 text-bone-200/50 mb-1">{label}</p>
        {children}
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest2 text-bone-200/60 mb-3">
        {label}{required && <span className="text-clay-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-white/15 rounded-md px-4 py-3.5 text-bone-100 placeholder:text-bone-200/30 focus:outline-none focus:border-clay-400 transition-colors"
      />
    </div>
  );
}
